import { ReportStatus, type Report, type Routine } from '@littlebird/sdk';

export type ReadyReport = {
  kind: 'ready';
  heading?: string;
  body: string;
  markdownId: string;
  scheduledAt: Report['scheduledAt'];
  source: Report;
};

export type RoutineReport =
  | { kind: 'loading' }
  | { kind: 'generating' }
  | { kind: 'empty' }
  | ReadyReport;

const splitReportHeading = (
  markdown: string
): { heading?: string; body: string } => {
  const trimmed = markdown.trimStart();
  const lineEnd = trimmed.indexOf('\n');
  const firstLine = lineEnd === -1 ? trimmed : trimmed.slice(0, lineEnd);
  const match = firstLine.match(/^#{1,6}\s+(.+?)\s*#*$/);
  if (!match) return { body: markdown };
  const body = lineEnd === -1 ? '' : trimmed.slice(lineEnd + 1).trimStart();
  return { heading: match[1].trim(), body };
};

export const routineReportOf = (
  isLoading: boolean,
  latest: Report | undefined
): RoutineReport => {
  if (isLoading) return { kind: 'loading' };
  const text = latest?.text.trim() ?? '';
  if (text === ReportStatus.GENERATING) return { kind: 'generating' };
  if (!latest || text === '' || text === ReportStatus.NO_DATA)
    return { kind: 'empty' };
  return {
    kind: 'ready',
    ...splitReportHeading(text),
    markdownId: `pinned-routine-${latest.referenceId}`,
    scheduledAt: latest.scheduledAt,
    source: latest,
  };
};

const PENDING_LABEL = {
  loading: 'Loading…',
  generating: 'Generating…',
  empty: 'No report yet',
} as const;

export const routineReportLabel = (report: RoutineReport): string =>
  report.kind === 'ready'
    ? (report.heading ?? 'Latest report')
    : PENDING_LABEL[report.kind];

export const latestRoutineReport = (
  reports: readonly Report[],
  routineId: number
): Report | undefined =>
  reports
    .filter((report) => report.reportTemplateId === routineId)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

/**
 * The routines that can show as pins, for both the Home section and its picker.
 * A paused routine keeps its pin, so it comes back when resumed.
 */
export const pinnableRoutines = <T extends Pick<Routine, 'isPaused'>>(
  visibleRoutines: readonly T[]
): T[] => visibleRoutines.filter((routine) => !routine.isPaused);
